import Big from 'big.js'

import type { CalculatedDocumentItem, DocumentItemTaxTotal } from '$lib/components/document/doc_types'
import type {
	CalculatedInvoice,
	Invoice,
	InvoiceTax,
	Payment
} from '$lib/types/client_types'

import { CalculationType } from '../../types'
import type { DocShippingItem } from '../doc_shipping_items/types'

export function calculateInvoice(doc: Invoice, payments: Payment[]): CalculatedInvoice {
	// unusual case in this function is to make it easier to mesh with server side
	// which is all snake-case
	/*
    ITEMS (in `items`)

    SUBTOTAL (in `subtotal`)
    -----------------
    PRE-TAX DISCOUNTS (in `discounts`, summed in `pre_tax_total`)
    TAXES (in `taxes`)
    -----------------
    -----------------
    POST-TAX TOTAL (in `post_tax_total`)
    -----------------
    POST-TAX DISCOUNTS (in `discounts`, NOT summed)
    -----------------
    -----------------
    GRAND TOTAL (in `total`)
    */



	// Subtotal is required early to 'scale' fixed discounts onto items
	const subtotal_items = doc.items
		// note that quantity is not a Big it is a numeric, so quantity.times(...)
		// would be a mistake
		.map(({ price, quantity }) => price.times(quantity).round(2))
		.reduce(add, Big(0))

	// Subtotal is required early to 'scale' fixed discounts onto items
	const subtotal_shipping_items = doc.shipping_items
		// note that quantity is not a Big it is a numeric, so quantity.times(...)
		// would be a mistake
		.map(({ price }) => price.round(2))
		.reduce(add, Big(0))

	const subtotal = subtotal_items.add(subtotal_shipping_items)


	// Lookup table for items to calculate tax
	const tax_lookup: Record<string, Big> = {}
	// console.log('1011-calculated',invoice.items[0].details)
	//console.log('div taxes:',invoice.taxes)
	doc.taxes.forEach((tax) => {
		// console.log('div:', tax.rate)
		if (tax.rate) tax_lookup[tax.id] = tax.rate.div(100)
	})

	// Generate decorated items
	const items = doc.items.map((item): CalculatedDocumentItem => {
		const item_subtotal = item.price.times(item.quantity).round(2)

		// now apply discounts that are pre-tax to this item
		const pre_tax_discount = doc.pre_tax_discounts
			.map((discount) => {
				if (discount.type === CalculationType.Rate) {
					return discount.rate.div(100).times(item_subtotal).round(2)
				} else if (discount.type === CalculationType.Fixed) {
					// *scale* discount to this item from invoice subtotal
					return subtotal.eq(0)
						? Big(0)
						: discount.fixed.div(subtotal).times(item_subtotal).round(2)
				} else {
					throw new Error(`Unknown discount calculation type: ${discount.type}`)
				}
			})
			.reduce(add, Big(0))

		// total after pre tax discounts
		const pre_tax_total = item_subtotal.minus(pre_tax_discount)

		// now apply taxes
		let total_tax = Big(0)
		const tax_totals: DocumentItemTaxTotal = {}
		item.taxes.forEach((tax_id) => {

			const tax_aux = tax_lookup[tax_id]
			if (tax_aux) {
				const tax = pre_tax_total.times(tax_aux).round(2)
				tax_totals[tax_id] = tax
				total_tax = total_tax.plus(tax)
			}
 
		})

		// total could be misleading here, so watch out
		// pre AND post tax discounts still have to be applied globally
		// even though pre tax discounts were used to find total tax
		const total = pre_tax_total.plus(total_tax)

		return {
			...item,
			subtotal: item_subtotal,
			tax: total_tax,
			tax_totals,
			total
		}
	})


	// Generate decorated shipping_items
	const shipping_items = doc.shipping_items.map((item): DocShippingItem => {
		// const item_subtotal = item.price.round(2)
		const item_subtotal = Big(item.price).round(2)

		// now apply discounts that are pre-tax to this item
		const pre_tax_discount = doc.pre_tax_discounts
			.map((discount) => {
				if (discount.type === CalculationType.Rate) {
					return discount.rate.div(100).times(item_subtotal).round(2)
				} else if (discount.type === CalculationType.Fixed) {
					// *scale* discount to this item from invoice subtotal
					return subtotal.eq(0)
						? Big(0)
						: discount.fixed.div(subtotal).times(item_subtotal).round(2)
				} else {
					throw new Error(`Unknown discount calculation type: ${discount.type}`)
				}
			})
			.reduce(add, Big(0))

		// total after pre tax discounts
		const pre_tax_total = item_subtotal.minus(pre_tax_discount)

		// now apply taxes
		let total_tax = Big(0)
		const tax_totals: DocumentItemTaxTotal = {}
		item.taxes.forEach((tax_id) => {
			const tax = pre_tax_total.times(tax_lookup[tax_id]).round(2)
			tax_totals[tax_id] = tax
			total_tax = total_tax.plus(tax)
		})

		// total could be misleading here, so watch out
		// pre AND post tax discounts still have to be applied globally
		// even though pre tax discounts were used to find total tax
		const total = pre_tax_total.plus(total_tax)

		return {
			...item,
			subtotal: item_subtotal,
			tax: total_tax,
			tax_totals,
			total
		}
	})

	const pre_tax_discounts = doc.pre_tax_discounts.map((discount) => {
		if (discount.type === CalculationType.Rate) {
			return {
				...discount,
				total: discount.rate.div(100).times(subtotal).round(2)
			}
		} else if (discount.type === CalculationType.Fixed) {
			return {
				...discount,
				total: discount.fixed
			}
		} else {
			throw new Error(`Unknown discount calculation type: ${discount.type}`)
		}
	})

	const pre_tax_discount = pre_tax_discounts.map(({ total }) => total).reduce(add, Big(0))
	const pre_tax_total = subtotal.minus(pre_tax_discount)

	const post_tax_total_items = items.map(({ total }) => total).reduce(add, Big(0))
	const post_tax_total_shippings = shipping_items.map(({ total }) => total).reduce(add, Big(0))

	let post_tax_total = post_tax_total_items.add(post_tax_total_shippings)
	// let post_tax_total = items.map(({ total }) => total).reduce(add, Big(0))
	if (post_tax_total.eq(0)) {
		post_tax_total = pre_tax_total
	}

	const taxes = doc.taxes.map((tax) => ({
		...tax,
		total: items.map(({ tax_totals }) => tax_totals[tax.id] || Big(0)).reduce(add, Big(0)).add(
				   shipping_items.map(({ tax_totals }) => tax_totals[tax.id] || Big(0)).reduce(add, Big(0))
		)
	}))
	const tax = taxes.reduce((a: Big, t: InvoiceTax) => a.plus(t.total), Big(0))

	const post_tax_discounts = doc.post_tax_discounts.map((discount) => {
		if (discount.type === CalculationType.Rate) {
			return {
				...discount,
				total: discount.rate.div(100).times(post_tax_total)
			}
		} else if (discount.type === CalculationType.Fixed) {
			return {
				...discount,
				total: discount.fixed
			}
		} else {
			throw new Error(`Unknown discount calculation type: ${discount.type}`)
		}
	})

	// const post_tax_discount = post_tax_discounts.map(({ total }) => total).reduce(add, Big(0))


	// Grand total of an invoice must be a payable amount
	// const grand_total = post_tax_total.minus(post_tax_discount).round(2);
	const grand_total = subtotal.minus(pre_tax_discount).plus(tax)

	// console.log('debug: post_tax_total', Number(post_tax_total))
	// console.log('debug: grand_total', Number(grand_total))
	// console.log('debug: estimate grand_total:', Number(subtotal.minus(pre_tax_discount).plus(tax)))

	const deposits = doc.deposits.map((deposit) => {
		let total = Big(0)
		if (deposit.type === CalculationType.Rate) {
			// Deposits must be a payable amount
			total = deposit.rate.div(100).times(grand_total).round(2)
		} else if (deposit.type === CalculationType.Fixed) {
			total = deposit.fixed
		} else {
			throw new Error(`Unknown deposit calculation type: ${deposit.type}`)
		}
		return { ...deposit, total }
	})
	const deposit_total = deposits.map(({ total }) => total).reduce(add, Big(0))
	const payment_total = payments.map(({ total }) => total).reduce(add, Big(0))
	const balance = grand_total.minus(payment_total)
	const deposit_due = deposit_total.minus(payment_total)

	// console.log('debug: grand_total', Number(grand_total))
	// console.log('debug: deposit_total', Number(deposit_total))
	// console.log('debug: payment_total', Number(payment_total))
	// console.log('debug: balance', Number(balance))
	// console.log('debug: deposit_due', Number(deposit_due))

	return {
		...doc,
		items,
		shipping_items,
		deposits,

		subtotal, // basic line items added
		pre_tax_discounts, // discounts array[]
		pre_tax_total, // discount total amount

		taxes, // taxes array[]
		post_tax_total, // taxes total amount

		post_tax_discounts, // post_tax_discounts array[]
		grand_total, // after post tax discounts

		deposit_total, // all deposits (whether they are paid or not!)
		payment_total,

		deposit_due,
		balance
	}
}

export function calculateInvoiceOk(doc: CalculatedInvoice): boolean {


	const items_total = doc.items
		.map(({ total }) => total)
		.reduce(add, Big(0))

	const shipping_items_total = doc.shipping_items
		.map(({ total }) => total)
		.reduce(add, Big(0))

	const subtotal = items_total.add(shipping_items_total)

	const diff = doc.grand_total.minus(subtotal)
	const resultOk = !diff.gt('0.5')

	if(!resultOk) {
		console.error('#1384 items shipp doc.total:', 
			items_total.toString(),
			shipping_items_total.toString(),
			doc.grand_total.toString())
	}
	return resultOk
}

function add(a: Big, b: Big) {
	return a.plus(b)
}
